import { LoopButton, Typography } from '@loophealth/loop-ui-web-library';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { InfoDefaultIcon } from '../../../assets/img';
import {
    selectModularAlaCarteBenefits,
    selectModularCurrentStep,
    selectModularSelectedBasePlan,
    selectModularWalletBalance
} from '../../../redux/slices/ModularEnrolmentSlice/selectors';
import { ModularEnrolmentSteps } from '../../../redux/slices/ModularEnrolmentSlice/types';
import { formatAmount } from '../../../utils/common';
import {
    SegmentStepperPageTitles,
    StepperStepTitles,
    StepperStepTitlesNoAddons
} from '../../../utils/constants/ModularConstants';
import PageStepper from '../PageStepper';
import CostPremiumModal from '../PremiumBreakdownModal';
import useModularEnrolment from './hooks/useModularEnrolment';
import usePremiumBreakdown from './hooks/usePremiumBreakdown';
import PlanChangeModal from './PlanChangeModal';
import {
    StyledFooterBackButtonWrapper,
    StyledFooterInfoIcon,
    StyledModularEnrolmentFooter,
    StyledModularFooterCTA,
    StyledModularFooterCTAButton,
    StyledModularFooterLeft,
    StyledModularPremiumAmount,
    StyledModularPremiumAmountLoader
} from './styles';
import SubmitConfirmationModal from './SubmitConfirmationModal';
import MemberConfirmationModal from './MemberConfirmationModal';
import { Prompt } from 'react-router-dom';
import useSegment from '../../../utils/hooks/useSegment';
import { SEGMENT_ACTIONS } from '../../../utils/constants/SegmentActionConstants';
import { Loader } from '../../atoms';
import { getModularEnrolmentSummarySegmentProperties } from './utils';

const ModularEnrolmentFooter: React.FC = () => {
    const trackClick = useSegment('click');
    const currentStep = useSelector(selectModularCurrentStep);
    const selectedBasePlan = useSelector(selectModularSelectedBasePlan);
    const alaCarte = useSelector(selectModularAlaCarteBenefits);
    const balance = useSelector(selectModularWalletBalance);
    const [stepTitles, setStepTitles] = React.useState(StepperStepTitles);

    const [costModalVisible, setCostModalVisible] = React.useState(false);

    const showPremiumAmount = currentStep !== ModularEnrolmentSteps.SUMMARY && selectedBasePlan;
    const showSaveForLater = currentStep === ModularEnrolmentSteps.SUMMARY;
    const showBackButton = currentStep !== ModularEnrolmentSteps.BASE_PLAN;

    const {
        isProceedEnabled,
        isSubmitting,
        isUpdating,
        isProcessing,
        submitModalVisible,
        confirmDeletions,
        confirmMemberModalVisible,
        setConfirmMemberModalVisible,
        onModularPlanNext,
        onDependentsNext,
        onAlaCarteBenefitsNext,
        handleOnSubmit,
        handleOnConfirmUpdation,
        setSubmitModalVisible,
        setConfirmDeletions,
        membersAdded,
        onConfirmMembers,
        handleBackClick,
        onSaveForLaterClick
    } = useModularEnrolment();

    const { modular, alacarte, priceSummary, isLoading } = usePremiumBreakdown();
    const isWalletUsed = priceSummary.walletDeducted > 0;

    const onProceedClick = () => {
        const clickAction = SEGMENT_ACTIONS.CLICK.PROCEED;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                page_name: SegmentStepperPageTitles[currentStep],
                page_category: SegmentStepperPageTitles[currentStep],
                ...getModularEnrolmentSummarySegmentProperties(
                    {
                        modular: modular,
                        alacarte: alacarte,
                        priceSummary: priceSummary
                    },
                    balance
                )
            }
        });
        if (isProcessing) return;
        switch (currentStep) {
        case ModularEnrolmentSteps.BASE_PLAN:
            return onModularPlanNext();
        case ModularEnrolmentSteps.DEPENDENTS:
            return onDependentsNext();
        case ModularEnrolmentSteps.HEALTHCARE_BENEFITS:
            return onAlaCarteBenefitsNext();
        case ModularEnrolmentSteps.SUMMARY:
            return setSubmitModalVisible(true);
        }
    };

    const handleOnSubmitClick = () => {
        const clickAction = SEGMENT_ACTIONS.CLICK.COMPLETE_REGISTRATION;
        const pageAction = SEGMENT_ACTIONS.PAGE.REVIEW_REGISTRATION_MODAL;
        trackClick({
            name: clickAction.name.toLowerCase(),
            properties: {
                ...pageAction.properties,
                ...getModularEnrolmentSummarySegmentProperties(
                    {
                        modular: modular,
                        alacarte: alacarte,
                        priceSummary: priceSummary
                    },
                    balance
                )
            }
        });
        handleOnSubmit();
    };

    const onInfoIconClick = () => setCostModalVisible(true);

    const isInProgress = !submitModalVisible;

    useEffect(() => {
        if (isInProgress) {
            const beforeUnloadCallback = async (event: BeforeUnloadEvent) => {
                event.preventDefault();
                return true;
            };
            window.addEventListener('beforeunload', beforeUnloadCallback);
            return () => {
                window.removeEventListener('beforeunload', beforeUnloadCallback);
            };
        }
    }, [isInProgress]);

    useEffect(() => {
        if (!alaCarte.loading && !alaCarte.data?.length) setStepTitles(StepperStepTitlesNoAddons);
        else setStepTitles(StepperStepTitles);
    }, [alaCarte]);
    const currentStepperStep = Math.min(stepTitles.length - 1, currentStep);

    return (
        <StyledModularEnrolmentFooter>
            <Prompt when={isInProgress} message="Wait! Are you sure you want to leave?" />
            <StyledModularFooterLeft>
                {showBackButton && (
                    <StyledFooterBackButtonWrapper>
                        <LoopButton variant="secondary" onClick={handleBackClick}>
                            Back
                        </LoopButton>
                    </StyledFooterBackButtonWrapper>
                )}
                <PageStepper
                    totalSteps={stepTitles.length}
                    currentStep={currentStepperStep}
                    text={stepTitles[currentStepperStep]}
                />
            </StyledModularFooterLeft>
            <StyledModularFooterCTA>
                {showPremiumAmount && (
                    <StyledModularPremiumAmount>
                        {isLoading ? (
                            <StyledModularPremiumAmountLoader>
                                <Loader />
                            </StyledModularPremiumAmountLoader>
                        ) : (
                            <>
                                <Typography variant="small">To Pay:</Typography>
                                {isWalletUsed && (
                                    <Typography variant="medium" weight="medium">
                                        <s>{formatAmount(priceSummary.totalPremium)}</s>
                                    </Typography>
                                )}
                                <Typography variant="large" color="emerald" weight="semiBold">
                                    {formatAmount(
                                        isWalletUsed ? priceSummary.salaryDeducted : priceSummary.totalPremium
                                    )}
                                </Typography>
                                <StyledFooterInfoIcon src={InfoDefaultIcon} onClick={onInfoIconClick} />
                            </>
                        )}
                    </StyledModularPremiumAmount>
                )}
                {showSaveForLater && (
                    <StyledModularFooterCTAButton>
                        <LoopButton variant="secondary" onClick={onSaveForLaterClick}>
                            Save for Later
                        </LoopButton>
                    </StyledModularFooterCTAButton>
                )}
                <StyledModularFooterCTAButton>
                    <LoopButton
                        variant={isProceedEnabled ? 'filled' : 'disabled'}
                        onClick={onProceedClick}
                        isLoading={isProcessing}
                    >
                        Proceed
                    </LoopButton>
                </StyledModularFooterCTAButton>
            </StyledModularFooterCTA>
            <CostPremiumModal
                title="Premium Breakdown"
                isVisible={costModalVisible}
                setIsVisible={setCostModalVisible}
                alacarte={alacarte}
                modular={modular}
                priceSummary={priceSummary}
                isEnrolmentOpen
            />
            <SubmitConfirmationModal
                isVisible={submitModalVisible}
                setIsVisible={setSubmitModalVisible}
                onSubmit={handleOnSubmitClick}
                isLoading={isSubmitting}
            />
            <PlanChangeModal
                isVisible={!!confirmDeletions}
                onClose={() => setConfirmDeletions(null)}
                deletions={confirmDeletions}
                onConfirm={handleOnConfirmUpdation}
                isUpdating={isUpdating}
            />
            <MemberConfirmationModal
                isVisible={confirmMemberModalVisible}
                setIsVisible={setConfirmMemberModalVisible}
                onSubmit={onConfirmMembers}
                totalMembers={membersAdded().totalMembers}
                addedMembers={membersAdded().addedMembers}
            />
        </StyledModularEnrolmentFooter>
    );
};

export default ModularEnrolmentFooter;
